import React from 'react';
import { Plus, CheckSquare, ShoppingCart, FileText, UserPlus, Clock, MessageSquare } from 'lucide-react';

/**
 * MobileFAB — Floating Action Button that adapts to the current page.
 * Sits above the bottom-nav (safe-area aware). One-tap to the highest-value
 * action on whichever module the user is on.
 *
 * @param {string} activeMenu current top-level menu key
 * @param {(action: string) => void} onAction lift the action back up so the
 *        parent can open modals / route to sub-pages without this component
 *        knowing anything about them.
 */
export default function MobileFAB({ activeMenu, onAction, portalType = 'admin' }) {
  const map = {
    admin: {
      tasks: { action: 'new-task', icon: CheckSquare, label: 'Add task' },
      accounting: { action: 'new-po', icon: ShoppingCart, label: 'New PO' },
      clients: { action: 'new-client', icon: UserPlus, label: 'Add client' },
      organisation: { action: 'new-employee', icon: UserPlus, label: 'Add employee' },
      helpdesk: { action: 'new-ticket', icon: MessageSquare, label: 'New ticket' },
      notices: { action: 'new-notice', icon: FileText, label: 'New notice' },
    },
    employee: {
      tasks: { action: 'new-task', icon: CheckSquare, label: 'Add task' },
      attendance: { action: 'check-in', icon: Clock, label: 'Check in' },
      helpdesk: { action: 'new-ticket', icon: MessageSquare, label: 'New ticket' },
    },
    client: {
      helpdesk: { action: 'new-ticket', icon: MessageSquare, label: 'New ticket' },
    },
  };

  const cfg = map[portalType]?.[activeMenu];
  if (!cfg) return null;

  const Icon = cfg.icon;
  const palette = {
    admin: 'bg-gradient-to-br from-fuchsia-500 to-pink-600 shadow-fuchsia-500/40',
    employee: 'bg-gradient-to-br from-amber-500 to-orange-600 shadow-amber-500/40',
    client: 'bg-gradient-to-br from-blue-500 to-indigo-600 shadow-blue-500/40',
  }[portalType] || 'bg-gradient-to-br from-fuchsia-500 to-pink-600';

  return (
    <button
      onClick={() => onAction?.(cfg.action)}
      aria-label={cfg.label}
      data-testid={`mobile-fab-${cfg.action}`}
      className={`fixed right-4 z-40 flex items-center gap-2 pl-4 pr-5 py-3.5 rounded-full text-white font-semibold text-sm shadow-2xl active:scale-95 transition-transform ${palette}`}
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4.75rem)' }}
    >
      <Icon className="w-5 h-5" />
      <span>{cfg.label}</span>
      <Plus className="w-4 h-4 opacity-70 -ml-0.5" />
    </button>
  );
}
